import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from "lucide-react";

export default function VerifyEmail() {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Récupérer l'email depuis les paramètres de navigation ou la session
    const stateEmail = location.state?.email;
    const stateUserId = location.state?.userId;
    const statePassword = location.state?.password;
    
    if (stateEmail && stateUserId) {
      setEmail(stateEmail);
      setUserId(stateUserId);
      if (typeof statePassword === 'string') {
        setPassword(statePassword);
      }
    } else {
      // Essayer de récupérer depuis la session
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          setEmail(user.email ?? null);
          setUserId(user.id);
          
          // Si l'email est déjà confirmé, rediriger
          if (user.email_confirmed_at) {
            navigate('/dashboard');
          }
        } else {
          // Pas de session, rediriger vers login
          navigate('/login');
        }
      });
    }
  }, [location, navigate]);

  const handleVerify = async () => {
    if (code.length !== 6 || !email) return;

    setIsVerifying(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('verify-email-code', {
        body: { email, code }
      });

      if (error) throw error;

      if (result?.valid) {
        setIsVerified(true);

        // After self-service signup there is no session yet (Confirm email ON).
        // Sign in now that the email is confirmed.
        if (password) {
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          setPassword(null);
          if (signInError) {
            console.error('Sign-in after verification failed:', signInError);
            toast({
              title: "Email vérifié !",
              description: "Connectez-vous pour continuer.",
            });
            setTimeout(() => navigate('/login', { state: { email } }), 1500);
            return;
          }
        }

        toast({
          title: "Email vérifié !",
          description: "Votre email a été vérifié avec succès.",
        });

        // Redirect to onboarding instead of dashboard for new establishments
        setTimeout(() => {
          navigate('/onboarding');
        }, 2000);
      } else {
        toast({
          title: "Code invalide",
          description: result?.error || "Le code est invalide ou a expiré.",
          variant: "destructive",
        });
        setCode('');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la vérification.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !userId) return;

    setIsResending(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('send-verification-code', {
        body: { userId, email }
      });

      if (error) throw error;

      if (result?.success) {
        toast({
          title: "Code renvoyé",
          description: "Un nouveau code a été envoyé à votre adresse email.",
        });
        setCode('');
      } else {
        toast({
          title: "Erreur",
          description: result?.error || "Impossible de renvoyer le code.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Email vérifié !</h2>
            <p className="text-muted-foreground">Redirection vers la configuration...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Vérification de l'email</CardTitle>
          <CardDescription>
            Entrez le code à 6 chiffres envoyé à{' '}
            <span className="font-medium text-foreground">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              value={code}
              onChange={setCode}
              maxLength={6}
              disabled={isVerifying}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || isVerifying}
            className="w-full"
          >
            {isVerifying ? "Vérification..." : "Vérifier le code"}
          </Button>

          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Vous n'avez pas reçu le code ?
            </p>
            <Button
              variant="outline"
              onClick={handleResendCode}
              disabled={isResending}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? "Envoi en cours..." : "Renvoyer le code"}
            </Button>
          </div>

          <div className="pt-4 border-t">
            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="w-full gap-2 text-muted-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour à la connexion
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
